import {
  all,
  call,
  delay,
  fork,
  put,
  putResolve,
  race,
  select,
  spawn,
  take,
} from 'redux-saga/effects';
import {
  CreativeDocumentContent,
  CreativeDocumentElement,
  CreativeDocumentMedium,
  Point,
  Rectangle,
} from 'content-creation-common/models';
import { getElementTransformAtFrame } from 'content-creation-common/utils/transform';
import { getGuid } from 'content-creation-common/utils/guid';
import { media as mediaConstants } from 'content-creation-common/constants';

import { isGroup } from '../../features/group/util';
import * as ElementSelectors from '../../selectors/element';
import * as UISelectors from '../../selectors/ui';
import * as ContentSelectors from '../../selectors/content';
import * as MediaSelectors from '../../selectors/media';
import { getZoomScale } from '../../features/playback/state-management/selectors';
import * as KeyframeSelectors from '../../selectors/keyframe';
import * as TextSelectors from '../../selectors/text';
import * as DomSelectors from '../../selectors/dom';
import {
  getRootEndFrame,
  isBuffering,
} from '../../features/shared/playback/state-management/selectors';
import { inSimpleFlow } from '../../features/routes/state-management/selectors';
import {
  getBoundingBoxByElementId,
  getIsInitialized as isSimpleFlowInitialized,
  getLayerByElementId,
} from '../../features/editor/simple/state-management/selectors';
import { isLoadingCreativeFields } from '../../features/creative-content/state-management/selectors';
import * as Actions from '../../actions/actions';
import DISCOVER_ACTION_CREATORS from '../../features/discover/state-management/actionCreators';
import {
  AUDIBLE_CONTENT_TYPES,
  CONTENT_TYPE,
  DEFAULT_PUBLISHER_CREATIVE_LENGTH,
  ERROR_CALL_TO_ACTION_MAP,
  ERROR_MAP,
  FIT_MODES,
  GLOBAL_ID,
  GLOBAL_FONT_CSS_URL,
  GROUP_TYPE,
  IMPORTED_SNAP_BACKGROUND_CREATIVE_FIELD,
  MEDIA_GALLERY_TABS,
  MEDIA_TYPE,
  MIN_MEDIA_SYNC_TIMEOUT,
  MINUTE_IN_SECONDS,
  PHOTO_AD_SYNC_TIMEOUT,
  PLAYBACK_FRAMES_PER_SECOND,
  POLL_RATE_MILLIS,
  SCALE_MAX,
} from '../../constants';
import { DEFAULT_FONT_PSNAME } from '../../features/text/constants';
import {
  DEFAULT_KEYFRAME_NUMBER,
  DEFAULT_KEYFRAME_TRANSFORM,
} from '../../features/creative/constants';
import { IS_EMBEDDED } from '../../features/projects/constants';
const { MEDIA_PROCESSORS } = mediaConstants;

import Layer from '../../model/Layer';
import * as MediaUtils from '../../util/mediaUtils';
import { filterInvalidFonts } from '../../util/font';
import { loadFont } from '../../api/fontApi';
import { colorInformation } from '../../util/color';
import { error, warn } from '../../util/log';
import {
  deepCopy,
  isArray,
  isBoolean,
  isNil,
  isNotNil,
} from '../../util/object';
import MediaMetadataError from '../../errors/MediaMetadataError';
import EmbedError from '../../errors/EmbedError';
import API from '../../api';
import { clamp, isNumber, isNumeric } from '../../util/numbers';
import { sendEvent } from '../../analytics/ga';
import { isEmpty, isNotEmpty as isNotEmptyArray } from '../../util/array';
import {
  getAllCreativeBuildErrors,
  getCreativeRectangle,
} from '../../features/creative/state-management/selectors';
import CONTENT_ACTION_CREATORS from '../../features/content/state-management/actionCreators';
import CREATIVE_ACTION_CREATORS from '../../features/creative/state-management/actionCreators';
import CREATIVE_CONTENT_ACTION_CREATORS from '../../features/creative-content/state-management/actionCreators';
import EDITOR_ACTION_CREATORS from '../../features/editor/shared/state-management/actionCreators';
import IMPORTED_ASSET_ACTION_CREATORS from '../../features/imported-asset/state-management/actionCreators';
import PLAYBACK_ACTION_CREATORS from '../../features/playback/state-management/actionCreators';
import SHARED_CREATIVE_ACTION_CREATORS from '../../features/shared/creative/state-management/actionCreators';
import TEXT_ACTION_CREATORS from '../../features/text/state-management/actionCreators';
import KEYFRAME_MANIPULATION_ACTION_CREATORS from '../../features/layer-manipulation/keyframe/state-management/actionCreators';
import { isAKeyframeNumber } from '../../util/keyframe';
import { getRelativePosition } from '../../util/transform';
import { getEmptyCreativeState } from '../../features/creative/util';
import {
  getXTransformForCentering,
  isMediumScalable,
} from '../../features/media/util';
import { loadMedia as mediaLoaderLoadMedia } from '../../features/media/loader/MediaLoaderFactory';
import { enqueueGAEvent } from '../../features/analytics/eventQueue';
import ImportDiscoverAssetError from '../../errors/ImportDiscoverAssetError';
import MediaFormatError from '../../errors/MediaFormatError';
import { getLocalURIFromFile } from '../../util/file';
import {
  getMaxSnapDurationInSeconds,
  getMinSnapDurationInSeconds,
  getTimeRangeMaxFrame,
  getTimeRangeMinFrame,
} from '../../features/shared/creative/state-management/selectors';
import MediaDurationExceededError from '../../errors/MediaDurationExceededError';
import MEDIA_TRIMMER_ACTION_CREATORS from '../../features/media-trimmer/state-management/actionCreators';
import { PLAYBACK_SPEEDS } from '../../features/shared/timeline/controls/constants';
import {
  frameManagementPerformanceReview,
  MEASUREMENT_KEYS,
} from '../../features/debug/measure/PerformanceReview';
import {
  getCurrentFrame,
  setCurrentFrame as playbackSetCurrentFrame,
} from '../../features/playback/manager/PlaybackManager';
import FrameManager from '../../features/frame-extraction/FrameManager';
import MEDIA_ACTION_CREATORS from '../../features/media/state-management/actionCreators';
import VIDEO_REFORMATTING_ACTION_CREATORS from '../../features/video-reformatting/state-management/actionCreators';
import REVIEW_ACTION_CREATORS from '../../features/review/state-management/actionCreators';
import Medium from '../../model/Medium';
import Content from '../../model/Content';
import { getCanvasTextMeasurements } from '../../features/text/state-management/selectors';

export function* selectElement(elementId) {
  yield put({
    type: Actions.EDIT_ELEMENT,
    elementId,
  });
}

export function* deselectElement() {
  yield put(EDITOR_ACTION_CREATORS.stopEditingElement());
}

export function* showContextMenu() {
  yield put({
    type: Actions.TOGGLE_SHOW_ELEMENT_CONTEXT_OPTIONS,
    isVisible: true,
  });
}

export function* updateMediaObject(mediaId, properties) {
  yield put({
    type: Actions.UPDATE_MEDIA_PROPERTIES,
    mediaId,
    ...properties,
  });
}

export function* updateTextString(contentId, mediaId, textString) {
  yield put(TEXT_ACTION_CREATORS.setTextString(contentId, textString));
}

/**
 * Send action to set media upload complete
 * @param {Array<string>} mediaIds
 * @returns {IterableIterator<SimpleEffect<"PUT", PutEffectDescriptor<{uploadComplete: boolean, type: *, mediaId: *}>>>}
 */
export function* setMediaUploadCompleted(mediaIds) {
  yield put({
    type: Actions.MEDIA_UPLOAD_COMPLETED,
    mediaIds,
    uploadComplete: true,
  });
}

export function* addElement({ contentId, disableRecordForUndo, elementId }) {
  if (!disableRecordForUndo) yield recordForUndo();

  const state = yield select();
  const frameStart = getTimeRangeMinFrame(state);
  const frameEnd = getTimeRangeMaxFrame(state);

  const action = {
    contentId,
    elementId,
    frameEnd,
    frameStart,
    type: Actions.ADD_ELEMENT,
  };
  yield put(action);
}

export function* createElementObject(contentId, disableRecordForUndo = false) {
  const elementId = `element-${getGuid()}`;

  yield call(addElement, { contentId, elementId, disableRecordForUndo });
  yield put(PLAYBACK_ACTION_CREATORS.pause()); // Pause if playing

  return { elementId };
}

export function* updateCreativeBuildErrors(specificElementId) {
  const state = yield select();
  const creativeBuildErrors = getAllCreativeBuildErrors(state);
  const elementsById = ElementSelectors.getAllElementsById(state);
  if (!isEmpty(creativeBuildErrors)) {
    // clear errors for this element
    const creativeBuildErrorsUpdated = creativeBuildErrors.filter(
      (creativeBuildError) => {
        const { elementId: buildErrorElementId } = creativeBuildError;
        if (isNotNil(specificElementId)) {
          return buildErrorElementId !== specificElementId;
        }
        return isNotNil(elementsById[buildErrorElementId]);
      },
    );
    const creativeBuildErrorsAction = CREATIVE_ACTION_CREATORS.setCreativeBuildErrors(
      creativeBuildErrorsUpdated,
    );
    yield put(creativeBuildErrorsAction);
  }
}

export const getClonedLayer = (element, content, media) => {
  const { contentType } = content;
  const isGroupContentType = isGroup(contentType);

  // Create new content
  const creativeContent = CreativeDocumentContent.fromJSON(content);
  creativeContent.contentId = CreativeDocumentContent.getId();

  // remove children if content is an element group
  // children are added back in elsewhere (see onCloneElement)
  // TODO move child duplication here?
  if (isGroupContentType) {
    creativeContent.contentElementIds = [];
  }

  // Create new media, can be null.
  let creativeMedium = null;

  // Create new media object but not for groups
  if (!isGroupContentType) {
    creativeMedium = CreativeDocumentMedium.fromJSON(media);
    creativeMedium.mediaId = CreativeDocumentMedium.getId();
    creativeContent.mediaId = creativeMedium.mediaId;
  }

  const creativeElement = CreativeDocumentElement.fromJSON(element);
  creativeElement.elementId = CreativeDocumentElement.getId();
  creativeElement.contentId = creativeContent.contentId;

  return Layer.getLayerFromCreativeDocumentObjects(
    creativeContent,
    creativeElement,
    creativeMedium,
  );
};

export function* initElementScale(
  elementId,
  mediaId,
  fitMode = FIT_MODES.FILL,
) {
  const state = yield select();
  const media = MediaSelectors.getById(state, mediaId);

  if (isNil(media)) return;
  if (isNil(FIT_MODES[fitMode])) return;

  const creativeRectangle = getCreativeRectangle(state);
  const height = MediaSelectors.getHeight(state, mediaId);
  const width = MediaSelectors.getWidth(state, mediaId);
  const mediumRectangle = new Rectangle(0, 0, width, height);
  const scale =
    fitMode === FIT_MODES.FILL
      ? mediumRectangle.getScaleToFillIn(creativeRectangle)
      : mediumRectangle.getScaleToFitIn(creativeRectangle);

  yield put({
    type: Actions.SET_SCALE,
    scaleX: scale,
    scaleY: scale,
    transformElementId: elementId,
    currentFrame: -1,
    applyToAll: true,
  });
}

export function* updateElementScale(elementId, oldMediaId, mediaId) {
  const state = yield select();

  const element = ElementSelectors.getById(state, elementId);
  const oldMedia = MediaSelectors.getById(state, oldMediaId);
  const media = MediaSelectors.getById(state, mediaId);

  if (
    !element ||
    isNil(oldMedia) ||
    isNil(media) ||
    isNaN(oldMedia.width) ||
    isNaN(oldMedia.height) ||
    isNaN(media.width) ||
    isNaN(media.height)
  ) {
    return;
  }

  // Update scale based on previous media size and current size.
  // Get transform at -1
  const currentTransform = getElementTransformAtFrame(element, -1, true);

  if (!currentTransform) {
    return;
  }

  const currentScale = Math.max(
    currentTransform.scale.x,
    currentTransform.scale.y,
  );

  const scaledWidth = oldMedia.width * currentScale;
  const scaledHeight = oldMedia.height * currentScale;

  const ratioWidth = scaledWidth / media.width;
  const ratioHeight = scaledHeight / media.height;

  const newScale = Math.min(SCALE_MAX, Math.max(ratioHeight, ratioWidth)); // Don't upscale!

  yield put({
    type: Actions.SET_SCALE,
    scaleX: newScale,
    scaleY: newScale,
    transformElementId: elementId,
    currentFrame: -1,
    applyToAll: true,
  });
}

export function* horizontallyCenterImageOnSalientRegion(elementId, mediaId) {
  const state = yield select();

  const element = ElementSelectors.getById(state, elementId);
  const media = MediaSelectors.getById(state, mediaId);
  const centerX = MediaSelectors.getCenterX(state, mediaId);

  if (!element || !media || isNil(centerX)) return;

  const currentFrame = getCurrentFrame();
  const applyToAll = !isAKeyframeNumber(element, currentFrame);

  const { height: creativeHeight, width: creativeWidth } = getCreativeRectangle(
    state,
  );
  const scale = creativeHeight / media.height;
  const x = getXTransformForCentering(media, scale, creativeWidth);
  const y = 0;

  // parent transform
  const { groupId: parentElementId } = element;
  const parentTransform = isNotNil(parentElementId)
    ? KeyframeSelectors.getElementTransformWithParentTransformsAtCurrentFrame(
        state,
        parentElementId,
      )
    : DEFAULT_KEYFRAME_TRANSFORM;

  const { rotation: parentRotation, scale: parentScale } = parentTransform;
  const relativeTranslation = getRelativePosition(
    parentTransform,
    new Point(x, y),
  );

  const relativeScaleX = scale / parentScale.x;
  const relativeScaleY = scale / parentScale.y;
  yield put({
    type: Actions.SET_SCALE,

    applyToAll,
    currentFrame,

    scaleX: relativeScaleX,
    scaleY: relativeScaleY,
    transformElementId: elementId,
  });

  yield put({
    type: Actions.SET_POSITION,

    applyToAll,
    currentFrame,
    x: relativeTranslation.x,
    y: relativeTranslation.y,

    transformElementId: elementId,
  });

  const relativeRotation = 0 - parentRotation;
  yield put({
    type: Actions.SET_ROTATION,

    applyToAll,
    currentFrame,

    rotation: relativeRotation,
    transformElementId: elementId,
  });
}

export function* setContentStartEnd(contentId, start, end) {
  yield put({
    type: Actions.SET_CONTENT_START_END,
    contentId,
    end,
    start,
  });
}

export function* updateElementFrameRange(
  elementId,
  startFrame,
  endFrame,
  scrubTo = true,
) {
  // in simpleFlow, we always want to maintain the element's time range
  const state = yield select();
  if (!inSimpleFlow(state)) {
    yield put({
      type: Actions.UPDATE_ELEMENT_TIME_RANGE,
      elementId,
      scrubTo,
      end: Math.floor(endFrame),
      start: Math.floor(startFrame),
    });
  }
}

export function* fitTotalFramesToMedia(
  mediaId,
  minFrames = 0,
  maxFrames = Number.MAX_SAFE_INTEGER,
) {
  const state = yield select();
  let mediaDuration = MediaSelectors.getDuration(state, mediaId);

  // Check for associated content duration
  const contentArray = ContentSelectors.getContentByMediaId(state, mediaId);
  if (isNotEmptyArray(contentArray)) {
    // take longest duration
    const contentDuration = Math.max(
      ...contentArray.map((content) => content.end - content.start),
    );
    if (mediaDuration > contentDuration) {
      mediaDuration = contentDuration;
    }
  }

  if (isNumber(mediaDuration)) {
    const duration = clamp(mediaDuration, minFrames, maxFrames);
    const frameTotal = Math.floor(duration * PLAYBACK_FRAMES_PER_SECOND);
    yield put({
      type: Actions.UPDATE_TOTAL_FRAMES,
      total: frameTotal,
    });
  }
}

function* loadMedia(mediaId) {
  try {
    const state = yield select();
    return yield mediaLoaderLoadMedia(mediaId, state);
  } catch (error) {
    if (
      error instanceof MediaMetadataError ||
      error instanceof MediaFormatError
    ) {
      yield showErrorModal([
        ERROR_MAP.PROCESS_MEDIA,
        ERROR_CALL_TO_ACTION_MAP.TRY_DIFFERENT_FILE,
      ]);
    } else if (error instanceof MediaDurationExceededError) {
      const durationErrorMessage = Object.assign(ERROR_MAP.MEDIA_DURATION, {
        values: {
          maxDuration: Math.floor(error.maxDuration / MINUTE_IN_SECONDS),
        },
      });
      yield showErrorModal([
        durationErrorMessage,
        ERROR_CALL_TO_ACTION_MAP.TRY_DIFFERENT_FILE,
      ]);
    } else {
      yield showErrorModal([ERROR_MAP.DEFAULT, ERROR_CALL_TO_ACTION_MAP.RETRY]);
    }
    throw error;
  }
}

/**
 * Creates an initial layer in a new creative with media that is currently uploaded with the provided serverId
 *
 * @param serverMediaId - the ID of the media on the server (Media Entity)
 * @param useCenterX - allow init step to use center x to position server media
 */
export function* addInitialElementWithServerMedia(
  serverMediaId,
  useCenterX = true,
) {
  // when creating a new snap, adds an initial layer from existing server media
  if (isNil(serverMediaId)) return undefined;

  const mediaResponseData = yield call(API.getMediaById, serverMediaId);
  if (isNil(mediaResponseData) || isNil(mediaResponseData.data)) {
    return undefined;
  }

  const { data: serverMedia } = mediaResponseData;

  const {
    center_x: centerX,
    file_name: fileName,
    frame_format: frameFormat,
    id: serverId,
    media_type: mimeType,
    snap_source: source,
    uploadComplete,
    variants,
  } = serverMedia;

  // If upload is not complete, delay and try again.
  // TODO remove this race as a hack for infinite looping.
  if (!uploadComplete) {
    yield delay(POLL_RATE_MILLIS.GET_MEDIA);
    yield race({
      addComplete: call(addInitialElementWithServerMedia, serverId, useCenterX),
      addTimeout: delay(MIN_MEDIA_SYNC_TIMEOUT),
    });

    return undefined;
  }

  const mediaType = MediaUtils.getMediaTypeForMimeType(mimeType);

  const { contentId, elementId, mediaId, success } = yield addMediaAndElement({
    centerX,
    fileName,
    frameFormat,
    mediaType,
    mimeType,
    serverId,
    source,
    variants,
    url: `/api/v1/media/${serverId}/file`,
    createNewElement: true,
  });

  if (!success) {
    warn(`Failed to add media from server media ${mediaId}`);
    return undefined;
  }

  yield put(
    IMPORTED_ASSET_ACTION_CREATORS.importCompleted(
      elementId,
      contentId,
      mediaId,
    ),
  );

  const state = yield select();

  // TODO move this elsewhere
  if (UISelectors.isPhotoEditMode(state)) {
    sendEvent('UI', 'Photo Editor');
  }

  const media = MediaSelectors.getById(state, mediaId);

  // try to do automatic centering/scaling
  yield call(syncMedia, media);

  // If the media is an image, wait for the center X.
  if (mediaType === MEDIA_TYPE.IMAGE) {
    if (useCenterX && MediaSelectors.hasCenterX(media)) {
      yield horizontallyCenterImageOnSalientRegion(elementId, mediaId);
    } else {
      // wait for sync to complete, or a shorter timeout
      const { syncMediaComplete, syncTimeout } = yield race({
        syncMediaComplete: take(MEDIA_ACTION_CREATORS.syncMediaComplete),
        syncTimeout: delay(PHOTO_AD_SYNC_TIMEOUT),
      });

      // If sync was successful, try to center the image.
      if (isNotNil(syncMediaComplete)) {
        // get the updated state that should now have the centerX info
        const updatedState = yield select();
        const syncedMedia = MediaSelectors.getById(updatedState, mediaId);
        const mediaHasCenterX = MediaSelectors.hasCenterX(syncedMedia);
        if (useCenterX && mediaHasCenterX) {
          // center and scale
          yield horizontallyCenterImageOnSalientRegion(elementId, mediaId);
        }
      }

      // If the delay fired first, send an event to GA
      if (isNotNil(syncTimeout)) {
        enqueueGAEvent('Saga', 'UI', 'nonCompliantMediaError', {
          nonInteraction: true,
        });
      }
    }
  }

  // If the media is a video, set the duration of the Snap to the duration of the video.
  if (mediaType === MEDIA_TYPE.VIDEO) {
    const { number_of_frames: mediaFrames } = serverMedia;
    if (isNumber(mediaFrames) && mediaFrames > -1) {
      yield put({
        type: Actions.UPDATE_TOTAL_FRAMES,
        total: clamp(
          mediaFrames,
          getTimeRangeMinFrame(state),
          getTimeRangeMaxFrame(state),
        ),
      });
    } else {
      yield fitTotalFramesToMedia(
        mediaId,
        getMinSnapDurationInSeconds(state),
        getMaxSnapDurationInSeconds(state),
      );
    }
  }

  yield call(initElementScale, elementId, mediaId);

  // Remove the loading progress on the element - this is safe because,
  // presumably, before opening snap publisher the media was previously uploaded
  yield setMediaUploaded(mediaId, serverId, fileName, elementId);

  // close the media gallery
  yield call(toggleMediaGallery, false);

  return {
    elementId,
    contentId,
    mediaId,
  };
}

/*
  TODO(caskew): Refactor this into functional chunks and use composition!! (srsly)

  NOTE: Any media or content that gets orphaned as a result of being replaced
  will be removed during the save process as a final catch-all.
 */
export function* addMediaAndElement({
  audible: defaultAudible,
  centerX,
  createNewElement = false,
  duration,
  elementId,
  fileName,
  frameFormat,
  height,
  localUrl,
  mediaType,
  mediaUploaded,
  mimeType,
  serverId,
  source,
  url,
  variants,
  visible: defaultVisible,
  width,
}) {
  // Check mediaType for validity
  const contentType = MediaUtils.getContentTypeForMediaType(mediaType);
  if (!contentType) {
    error(`addMediaAndElement does not support media type: ${mediaType}`);
    return { success: false };
  }

  const startState = yield select();
  const previousLayer = ElementSelectors.getLayerById(startState, elementId);
  const previousMediaId = isNotNil(previousLayer)
    ? previousLayer.mediumId
    : undefined;

  // before we add the content, reset the playhead
  yield call(togglePlayback, false);

  // set the playhead back to this element.
  if (isNotNil(previousLayer)) {
    // if it already exists, scrub to the element
    yield put({
      type: Actions.SCRUB_TO_ELEMENT,
      elementId,
    });
  }

  // Create the media object in the model
  const mediaProperties = {
    centerX,
    fileName,
    frameFormat,
    localUrl,
    mediaType,
    mimeType,
    serverId,
    source,
    url,
    variants,
  };

  if (mediaUploaded) {
    mediaProperties.uploadFailed = false;
    mediaProperties.uploaded = true;
    mediaProperties.uploadedPercent = 1;
  }

  const medium = new Medium(mediaProperties);
  const content = new Content(contentType, null, medium);

  // if visibility property is explicitly declared by user, use it
  // else, check the content type if it is audio
  content.visible = isBoolean(defaultVisible)
    ? defaultVisible
    : contentType !== CONTENT_TYPE.AUDIO;
  content.audible = isBoolean(defaultAudible)
    ? defaultAudible
    : AUDIBLE_CONTENT_TYPES.has(contentType);

  // if video or audio set these properties
  if (
    contentType === CONTENT_TYPE.VIDEO ||
    contentType === CONTENT_TYPE.AUDIO
  ) {
    content.endTime = isNumeric(duration) ? duration : medium.duration;
    content.startTime = 0;
  }

  // default video's processor to MDP
  if (contentType === CONTENT_TYPE.VIDEO) {
    medium.processors = [MEDIA_PROCESSORS.MDP];
  }

  // if there was a creativeFieldId defined on the previous content, preserve it
  if (
    isNotNil(previousLayer) &&
    isNotNil(previousLayer.content.creativeFieldId)
  ) {
    content.creativeFieldId = previousLayer.content.creativeFieldId;
  }

  let finalLayer = previousLayer;

  // If there was not layer with the elementId, or if we are explicitly making a new layer
  if (isNil(finalLayer) || createNewElement) {
    // If no element id, create a new element with content id and content type.
    // This will temporarily create the appearance of a new layer in the media gallery which will reload the media drawer
    finalLayer = new Layer(content);
  } else {
    // If using a previous layer, overwrite the content
    finalLayer.content = content;
  }

  // SAVE LAYER
  // Save the state of the current layer to show state in the UI
  if (isNotNil(previousLayer)) {
    yield put(SHARED_CREATIVE_ACTION_CREATORS.updateLayers([finalLayer]));
  } else {
    yield put(SHARED_CREATIVE_ACTION_CREATORS.addLayers([finalLayer]));

    // Before we set the "new element request" to false (indicating we do not want to display a new layer in the media gallery) select that
    // layer so that react puts the new temporary layer into the same state as the existing layer we are modifying
    yield call(selectElement, finalLayer.id);
    // Now set the flag indicating that this element is just temporary and is not a new layer the user chose to add
    yield call(setNewElementRequest, false);
  }

  const layerState = yield select();

  try {
    // Figure out the media dimensions and duration.
    // It's either passed in, from parts passed in, from sync'd media, or loaded via the resource.
    if (isNumeric(duration) && isNumeric(height) && isNumeric(width)) {
      finalLayer.medium.duration = duration;
      finalLayer.medium.height = height;
      finalLayer.medium.width = width;
    } else {
      // LOAD MEDIA
      // Otherwise load the medium to get these values
      const mediaData = yield call(loadMedia, finalLayer.medium.mediaId);
      const {
        duration: mediaDuration,
        height: mediaHeight,
        width: mediaWidth,
      } = mediaData;
      finalLayer.medium.duration = mediaDuration;
      finalLayer.medium.height = mediaHeight;
      finalLayer.medium.width = mediaWidth;
    }

    // Presumably at this point loading it complete.
    finalLayer.medium.serverId = serverId;
    finalLayer.medium.loaded = true;

    // Explicitly override the width and height
    // We do this again here in case duration was missed above.
    if (isNumeric(width) && isNumeric(height)) {
      finalLayer.medium.height = height;
      finalLayer.medium.width = width;
    }

    // The previously created default clip/content does not yet have a defined
    // end time. Update that using the duration information from loadMedia.
    if (
      contentType === CONTENT_TYPE.VIDEO ||
      contentType === CONTENT_TYPE.AUDIO
    ) {
      content.startTime = 0;
      content.endTime = medium.duration;
    }

    // Update the elements frame range based on the duration
    // We ignore simple mode because there is no way to move the element in the timeline there.
    // In reality we should probably not do this in advanced mode either. I don't know why we do it.
    if (
      !inSimpleFlow(layerState) &&
      (contentType === CONTENT_TYPE.AUDIO || contentType === CONTENT_TYPE.VIDEO)
    ) {
      // set the a/v element to be the actual length without looping
      finalLayer.startFrame = 0;
      finalLayer.endFrame = Math.floor(
        finalLayer.medium.duration * PLAYBACK_FRAMES_PER_SECOND,
      );
    }

    // SAVE AGAIN
    // We save again here to make sure to propagate the new values, but it would be ideal to reduce the number of saves down
    // TODO figure out a way to not need to do this
    yield put(SHARED_CREATIVE_ACTION_CREATORS.updateLayers([finalLayer]));

    // Scrub to new layer, if created
    if (isNil(previousLayer)) {
      yield put({
        type: Actions.SCRUB_TO_ELEMENT,
        elementId: finalLayer.id,
      });
    }

    // Don't update scale if new.
    if (isMediumScalable(finalLayer.medium)) {
      if (isNotNil(previousLayer) && !createNewElement) {
        if (isSimpleFlowInitialized(layerState)) {
          const bounds = getBoundingBoxByElementId(layerState, finalLayer.id);
          yield call(
            positionToFitBoundingBox,
            finalLayer.id,
            bounds,
            previousMediaId,
            finalLayer.mediumId,
          );
        } else {
          yield call(
            updateElementScale,
            finalLayer.id,
            previousMediaId,
            finalLayer.mediumId,
          );
        }
      }
    }

    // Load color data about new layer
    if (
      contentType === CONTENT_TYPE.IMAGE ||
      contentType === CONTENT_TYPE.SVG
    ) {
      const finalMediaId = finalLayer.mediumId;
      const url = MediaSelectors.getUrlById(layerState, finalMediaId);
      yield spawn(setMediaColors, { localUrl, mediaId: finalMediaId, url });
    }

    // Update creative field
    if (isNotNil(previousLayer)) {
      yield put(
        CREATIVE_CONTENT_ACTION_CREATORS.updateCreativeFieldWithContent(
          elementId,
        ),
      );
    }

    return {
      contentId: finalLayer.contentId,
      elementId: finalLayer.id,
      mediaId: finalLayer.mediumId,
      success: true,
    };
  } catch (err) {
    error('Failed to add element and media', err);

    // Handle a failed new-layer media
    // For now, just delete the element. Ideally, this would roll back any
    // changes to the existing element in that case.
    yield put({
      type: Actions.DELETE_ELEMENT_SAGA,
      elementId: finalLayer.id,
    });

    yield call(setNewElementRequest, true);
  }

  return { success: false };
}

export function* setMediaUploaded(
  mediaId,
  serverId,
  fileName = 'asset',
  elementId = null,
) {
  const state = yield select();
  const creativeFieldId = ElementSelectors.getCreativeFieldId(state, elementId);
  yield put({
    type: Actions.MEDIA_UPLOADED,
    creativeFieldId,
    fileName,
    mediaId,
    serverId,
  });
}

export function* addContent(mediaId, contentType, properties = {}) {
  const contentId = `content-${getGuid()}`;

  yield put({
    ...properties,
    type: Actions.ADD_CONTENT,
    contentType,
    contentId,
    mediaId,
  });

  return contentId;
}

export function* wipeHistoryClean() {
  yield put({
    type: Actions.WIPE_HISTORY_CLEAN,
  });
}

export function* recordForUndo(group) {
  yield put({
    type: Actions.RECORD_FOR_UNDO,
    recordingGroup: group,
  });
}

export function* pauseAndRecordForUndo(group) {
  yield put(PLAYBACK_ACTION_CREATORS.pause());

  yield recordForUndo(group);
}

export function* historyUndo() {
  yield putResolve({
    type: Actions.HISTORY_UNDO,
  });
}

export function* historyRedo() {
  yield putResolve({
    type: Actions.HISTORY_REDO,
  });
}

/**
 *  A hook that is called after undo or redo so that other sagas can respond to the event.
 */
export function* undoOrRedoHook(previousState) {
  yield put({
    type: Actions.UNDO_REDO_HOOK,
    previousState,
  });
}

export function* splitRecordingGroup() {
  yield put({
    type: Actions.SPLIT_RECORDING_GROUP,
  });
}

export function* accountNotFoundError(accountType, accountId) {
  /*
   If the account could not be found and we are running in embed mode this is a fatal error.
   Logging an event in Google Analytics and throwing an error will cause a page load error.
   */
  if (IS_EMBEDDED) {
    throw new EmbedError('Could not find account', accountType, accountId);
  }

  yield showErrorModal([
    ERROR_MAP.ACCOUNT_NOT_FOUND,
    ERROR_CALL_TO_ACTION_MAP.DIFFERENT_ACCOUNT,
  ]);
}

export function* showErrorModal(
  errorMessages = [ERROR_MAP.DEFAULT],
  /* optional */ action = undefined,
  /* optional */ isForcedDismissible = false,
  /* optional */ elementList = undefined,
) {
  if (!isArray(errorMessages)) {
    errorMessages = [errorMessages];
  }

  const errorDescription = errorMessages
    .map((err, i) => `${i}. ${err.defaultMessage || err.message}`)
    .join('\n');

  const state = yield select();
  if (UISelectors.isErrorModalVisible(state)) {
    error(
      `Refusing to open multiple error modals. Errors: \n${errorDescription}`,
    );
  } else {
    yield put({
      type: Actions.SHOW_ERROR_MODAL,
      errorMessages,
      isForcedDismissible,
      elementList,
    });
  }

  if (action) {
    yield put(action);
  }
}

export function* setDefaultFont(action) {
  const { font } = action;
  const { psName } = font;

  const state = yield select();
  const {
    organizationId,
    publisherId,
    businessProfileId,
  } = UISelectors.getCurrentUserContext(state);

  try {
    // Wait for the font to load before proceeding
    const fontLoadAPIResponse = yield call(
      loadFont,
      font,
      [],
      organizationId,
      publisherId,
      businessProfileId,
    );
    yield setFontData(font, fontLoadAPIResponse);

    yield put({
      type: Actions.SET_DEFAULT_FONT,
      font,
    });
  } catch (err) {
    warn(`Failed to load font ${psName}`, err);
  }
}

export function* toggleMediaGallery(isVisible = null) {
  yield put({
    type: Actions.TOGGLE_MEDIA_GALLERY,
    isVisible,
  });
}

export function* setNewElementRequest(isRequested = false) {
  yield put({
    type: Actions.SET_NEW_ELEMENT_REQUESTED,
    isRequested,
  });
}

export function* startSavingAndBuilding() {
  yield put({
    type: Actions.SHOW_BUILD_PROGRESS_MODAL,
  });
  yield put({
    type: Actions.START_SAVING_AND_BUILDING,
  });
}

export function* finishSavingAndBuilding(isSuccess) {
  if (!isSuccess) {
    yield put({
      type: Actions.HIDE_BUILD_PROGRESS_MODAL,
    });
  }
  yield put({
    type: Actions.FINISH_SAVING_AND_BUILDING,
    isSuccess,
  });
}

export function* startPublishing() {
  yield put({
    type: Actions.START_PUBLISHING,
  });
}

export function* finishPublishing(isSuccess) {
  yield put({
    type: Actions.FINISH_PUBLISHING,
    isSuccess,
  });
}

function* loadCreativeFont(
  font,
  organizationId,
  publisherId,
  businessProfileId,
) {
  if (isNil(font)) {
    throw new Error('No Font Specified');
  }

  const { psName } = font;

  try {
    const fontLoadAPIResponse = yield call(
      loadFont,
      font,
      [],
      organizationId,
      publisherId,
      businessProfileId,
    );
    yield setFontData(font, fontLoadAPIResponse);
  } catch (err) {
    warn(`Failed to load font ${psName}`, err);
  }
}

function* fetchFonts(groupType, groupId) {
  const { data: fonts, error: fontError, success: fontSuccess } = yield call(
    API.loadFonts,
    groupType,
    groupId,
  );

  if (fontError || !fontSuccess || !Array.isArray(fonts)) {
    error('could not load fonts [parse font names]', fontError);
    yield showErrorModal([
      ERROR_MAP.LOAD_FONTS,
      ERROR_CALL_TO_ACTION_MAP.REFRESH,
    ]);
    return [];
  }

  return fonts;
}

export function* loadFontList() {
  // Load the GLOBAL font list if it isn't already loaded
  const apiFontListStyleSheet = document.querySelector(
    `link[rel=stylesheet][href="${GLOBAL_FONT_CSS_URL}"]`,
  );
  if (isNil(apiFontListStyleSheet)) {
    const head = document.querySelector('head');
    const fontStyleSheet = document.createElement('link');
    fontStyleSheet.setAttribute('type', 'text/css');
    fontStyleSheet.setAttribute('rel', 'stylesheet');
    fontStyleSheet.setAttribute('href', GLOBAL_FONT_CSS_URL);
    head.appendChild(fontStyleSheet);
  }

  const state = yield select();
  const { organizationId, publisherId } = UISelectors.getCurrentUserContext(
    state,
  );
  const groupType = publisherId
    ? GROUP_TYPE.PUBLISHERS
    : GROUP_TYPE.ORGANIZATIONS;
  const groupId = publisherId || organizationId;

  const orgFonts =
    groupId && !UISelectors.isGlobalTemplate(state)
      ? yield fetchFonts(groupType, groupId)
      : [];
  const globalFonts = yield fetchFonts(GROUP_TYPE.ORGANIZATIONS, GLOBAL_ID);

  const fonts = [...orgFonts, ...globalFonts];

  const filteredFonts = filterInvalidFonts(fonts);

  yield put({
    type: Actions.SET_FONTS,
    fonts: filteredFonts,
  });

  if (filteredFonts.length === 0) {
    return;
  }

  const defaultFont = filteredFonts.find(
    ({ psName }) => psName === DEFAULT_FONT_PSNAME,
  );
  yield setDefaultFont({
    font: defaultFont || filteredFonts[0],
  });
}

export function* loadFontsInCreative(
  creative,
  organizationId,
  publisherId,
  businessProfileId,
) {
  yield loadFontList();

  const state = yield select();
  const { content } = creative;
  const { contentById } = content || {};

  const fontsToLoad = Object.keys(contentById)
    .map((contentId) => {
      const content = contentById[contentId];
      if (isNil(content) || CONTENT_TYPE.TEXT !== content.contentType) {
        return null;
      }
      const psName = content.font;
      const fontByPsName = TextSelectors.getFontByPSName(state, psName);
      // if we don't have the font in our font list, then try fetching it as archived
      return isNotNil(fontByPsName) ? fontByPsName : { psName, archived: true };
    })
    .filter((font) => font);

  if (isEmpty(fontsToLoad)) {
    return;
  }

  const loadFontTasks = Array.from(new Set(fontsToLoad)).map((font) =>
    call(
      loadCreativeFont,
      font,
      organizationId,
      publisherId,
      businessProfileId,
    ),
  );

  try {
    yield all(loadFontTasks);
  } catch (err) {
    warn('Unable to load some fonts in creative', err);
  }
}

export function* setSaveRequested(requested) {
  yield put(EDITOR_ACTION_CREATORS.saveRequested(requested));
}

export function* setAutoSaveRequested(requested) {
  yield put(EDITOR_ACTION_CREATORS.autoSaveRequested(requested));
}

export function* setSaveCancelled() {
  yield put(EDITOR_ACTION_CREATORS.saveCancelled());
}

export function* showMembershipSwitchModal(
  open,
  publisherId = null,
  accountId = null,
  organizationId = null,
  businessProfileId = null,
) {
  yield put({
    type: Actions.SHOW_MEMBERSHIP_SWITCH_MODAL,
    open,
    publisherId,
    accountId,
    organizationId,
    businessProfileId,
  });
}

export function* resetAccountId() {
  // Remove account id.
  yield put({
    type: Actions.SET_USER_CURRENT_ACCOUNT,
    account: null,
  });
  yield put({
    type: Actions.SET_USER_CURRENT_ORGANIZATION,
    organization: null,
  });
  // Reset media gallery tab
  yield put({
    type: Actions.SET_SELECTED_MEDIA_TAB,
    selectedMediaTab: MEDIA_GALLERY_TABS.MY_LIBRARY,
  });
}

export function* resetSnapProProfile() {
  yield put({
    type: Actions.SET_USER_CURRENT_SNAP_PRO_PROFILE,
    snapProProfile: null,
  });
  // Reset media gallery tab
  yield put({
    type: Actions.SET_SELECTED_MEDIA_TAB,
    selectedMediaTab: MEDIA_GALLERY_TABS.MY_LIBRARY,
  });
}

export function* resetPublisherId() {
  // Remove publisher id.
  yield put({
    type: Actions.SET_USER_CURRENT_PUBLISHER,
    publisher: null,
  });
  yield put({
    type: Actions.SET_USER_CURRENT_EDITION,
    edition: null,
  });
  yield put({
    type: Actions.SET_USER_CURRENT_SNAP_ID,
    snapId: null,
  });
  yield put(DISCOVER_ACTION_CREATORS.setDiscoverPublisher(null));
}

export function* setEditTemplate(template = null, isGlobal = false) {
  yield put({
    type: Actions.SET_EDIT_TEMPLATE,
    template,
    isGlobal,
  });
}

export function* setEditTemplateOriginalTags(editTemplateOriginalTags = null) {
  yield put({
    type: Actions.SET_EDIT_TEMPLATE_ORIGINAL_TAGS,
    editTemplateOriginalTags,
  });
}

export function* reorderElement(
  elementId = null,
  direction = null,
  oldIndex = null,
  newIndex = null,
  parentContentId = null,
) {
  if (direction === null) {
    yield put({
      type: Actions.REORDER_ELEMENT,
      oldIndex,
      newIndex,
    });
  } else {
    if (!parentContentId) {
      yield put({
        type: Actions.REORDER_ELEMENT_ONE_PLACE,
        elementId,
        direction,
      });
    } else {
      yield put({
        type: Actions.REORDER_ELEMENT_ONE_PLACE_WITHIN_GROUP,
        elementId,
        direction,
        parentContentId,
      });
    }
  }
}

export function* setCurrentFrame(frameNumber = null) {
  if (isNumber(frameNumber)) {
    playbackSetCurrentFrame(frameNumber);
  }
}

export function* scrubToFrame(frameNumber = null) {
  if (isNotNil(frameNumber)) {
    const state = yield select();
    const rootEndFrame = getRootEndFrame(state);
    const clampedFrameNumber = clamp(frameNumber, 0, rootEndFrame);
    yield put(PLAYBACK_ACTION_CREATORS.scrubToFrame(clampedFrameNumber));
  }
}

export function* toggleBuffering(shouldBuffer = false) {
  const state = yield select();
  const isPlaybackBuffering = isBuffering(state);
  if (shouldBuffer && !isPlaybackBuffering) {
    // We are starting buffering
    frameManagementPerformanceReview.start(
      MEASUREMENT_KEYS.TIME_SPENT_BUFFERING,
    );
  } else if (!shouldBuffer && isPlaybackBuffering) {
    // We are finishing buffering
    frameManagementPerformanceReview.end(MEASUREMENT_KEYS.TIME_SPENT_BUFFERING);
    frameManagementPerformanceReview.log();
  }
  yield put(
    shouldBuffer
      ? PLAYBACK_ACTION_CREATORS.startBuffering()
      : PLAYBACK_ACTION_CREATORS.stopBuffering(),
  );
}

export function* togglePlayback(shouldPlay = false) {
  if (shouldPlay) {
    yield put(PLAYBACK_ACTION_CREATORS.play());
  } else {
    yield put(PLAYBACK_ACTION_CREATORS.pause());
  }
}

export function* stopEditingText() {
  yield put(TEXT_ACTION_CREATORS.stopEditingText());
}

export function* calculateCanvasTextMeasurements(contentId) {
  const state = yield select();

  if (isNil(contentId)) {
    error('Invalid or empty content id provided', contentId);
    return;
  }

  const content = ContentSelectors.getById(state, contentId);
  if (!content) {
    return;
  }

  const { contentType } = content;

  if (contentType !== CONTENT_TYPE.TEXT) {
    warn('Cannot measure non text');
    return;
  }

  const layer = ElementSelectors.getLayerByContentId(state, contentId);
  const creativeLayer = Layer.getCreativeLayer(layer);

  const textRendererProperties = getCanvasTextMeasurements(creativeLayer);

  yield put(
    TEXT_ACTION_CREATORS.setTextNaturalHeight(
      contentId,
      textRendererProperties.textHeight,
    ),
  );
  yield put(
    TEXT_ACTION_CREATORS.setTextNaturalWidth(
      contentId,
      textRendererProperties.textWidth,
    ),
  );
  yield put(
    TEXT_ACTION_CREATORS.setRenderedFontSize(
      contentId,
      textRendererProperties.renderedFontSize,
    ),
  );
}

export function* setMostRecentColors(colors) {
  yield put({
    colors,
    type: Actions.SET_MOST_RECENT_COLORS,
  });
}

export function* addLocalFile(file, createNewElement = false) {
  const { name, type: mimeType } = file;
  const mediaType = MediaUtils.getMediaTypeForMimeType(mimeType);

  if (!mediaType) {
    throw new Error(`Unrecognized media type: ${mediaType}`);
  }

  const url = yield call(getLocalURIFromFile, file);
  yield put({
    createNewElement,
    file,
    fileName: name,
    mediaType,
    type: Actions.ADD_LOCAL_MEDIA,
    url,
  });
}

export function* setMediaColors({ localUrl, mediaId, url }) {
  try {
    const imgURL = url !== localUrl ? yield API.loadImageAsBlob(url) : localUrl;
    const colors = yield colorInformation(imgURL);
    yield put({
      type: Actions.SET_MEDIA_COLORS,
      colors,
      mediaId,
    });
  } catch (err) {
    warn(`Failed to load image ${mediaId}`, err);
  }
}

export function* addGroupElementObject(
  groupContentType = CONTENT_TYPE.ELEMENT_GROUP,
) {
  // add a new group content
  const contentId = yield addContent(null, groupContentType, {
    contentElementIds: [],
    isOpen: true,
  });

  // create a new element
  const { elementId: groupElementId } = yield createElementObject(
    contentId,
    true,
  );
  return groupElementId;
}

export function* addElementToGroup(elementId, groupId) {
  const state = yield select();

  const currentElement = ElementSelectors.getById(state, elementId);
  const { parentContentId: currentParentContentId, contentId } = currentElement;

  const groupElement = ElementSelectors.getById(state, groupId);
  const { contentId: parentContentId } = groupElement;

  yield put({
    type: Actions.MOVE_ELEMENT_TO_GROUP,
    contentId,
    currentParentContentId,
    elementId,
    groupId,
    parentContentId,
  });
}

export function* reorderElementV2(elementId, moveToElementId) {
  const state = yield select();
  const currentElement = ElementSelectors.getById(state, elementId);
  const moveToElement = ElementSelectors.getById(state, moveToElementId);

  yield put({
    type: Actions.REORDER_ELEMENT_V2,
    currentElement,
    moveToElement,
  });
}

/**
 * Create an element with content type GROUP_ELEMENT
 * Optional - you can pass in an elementId to nest it under new element,
 * this will replace that elementId with the group and add elementId as the child of the new group
 */
export function* createGroupElementForElement(elementId, groupContentType) {
  // add an empty group element object
  const groupElementId = yield addGroupElementObject(groupContentType);

  // nest element to the new group element
  if (elementId) {
    yield reorderElementV2(groupElementId, elementId);
    yield addElementToGroup(elementId, groupElementId);
  }

  return groupElementId;
}

export function* deleteGroupKeepContent(elementIdToDelete) {
  const state = yield select();
  const elementToDelete = ElementSelectors.getById(state, elementIdToDelete);
  const {
    contentId: contentIdToDelete,
    groupId: groupIdToDelete,
    parentContentId: parentContentIdToDelete,
  } = elementToDelete;

  // children needs to be updated
  const elementsToUpdate = ElementSelectors.getGroupChildElementIds(
    state,
    elementIdToDelete,
  );

  yield put({
    type: Actions.DELETE_GROUP_KEEP_CONTENTS,
    elementIdToDelete,
    elementsToUpdate,
    contentIdToDelete,
    groupIdToDelete,
    parentContentIdToDelete,
  });
}

export function* deleteGroupAndContent(elementIdToDelete) {
  const state = yield select();
  const elementToDelete = ElementSelectors.getById(state, elementIdToDelete);
  const {
    contentId: contentIdToDelete,
    groupId: groupIdToDelete,
    parentContentId: parentContentIdToDelete,
  } = elementToDelete;

  const descendantElementIds = ElementSelectors.getElementDescendants(
    state,
    elementIdToDelete,
  );
  const descendantContentIds = [];
  const descendantMediaIds = [];

  descendantElementIds.forEach((elementId) => {
    const contentId = ElementSelectors.getContentId(state, elementId);
    const mediaId = ElementSelectors.getMediaId(state, elementId);

    if (contentId) {
      descendantContentIds.push(contentId);
    }

    if (mediaId) {
      descendantMediaIds.push(mediaId);
    }
  });

  yield put({
    type: Actions.DELETE_GROUP_AND_CONTENTS,
    contentIdToDelete,
    descendantContentIds,
    descendantElementIds,
    descendantMediaIds,
    elementIdToDelete,
    groupIdToDelete,
    parentContentIdToDelete,
  });
}

export function* syncMedia(media, timeoutInMs = MIN_MEDIA_SYNC_TIMEOUT) {
  // Don't do anything for bad or processed media.
  if (
    isNil(media) ||
    isNil(media.serverId) ||
    MediaSelectors.hasBeenAnalysed(media)
  ) {
    return;
  }

  // No need to sync for different types
  const { mediaType } = media;
  if (mediaType !== MEDIA_TYPE.IMAGE && mediaType !== MEDIA_TYPE.VIDEO) return;

  // For images, kick off center X
  if (
    MediaSelectors.isUploadComplete(media) &&
    mediaType === MEDIA_TYPE.IMAGE
  ) {
    yield API.findImageCenter(media.serverId);
  }

  // Start polling for updates.
  yield put(
    MEDIA_ACTION_CREATORS.syncMedia(media.serverId, mediaType, timeoutInMs),
  );
}

export function* setFontData(font, fontLoadData) {
  if (!fontLoadData || !fontLoadData.success || !fontLoadData.data) {
    warn('Failed to load font data', font.psName);
    return;
  }

  const { data: fontArrayBuffer } = fontLoadData;
  const fontBlob = new Blob([new Uint8Array(fontArrayBuffer)]);

  const reader = new FileReader();
  const readerPromise = new Promise((resolve, reject) => {
    reader.onloadend = () => {
      resolve(reader.result);
    };

    reader.onerror = () => {
      reject(new Error(`Could not read font as Data URL from ${font.psName}`));
    };
  });
  reader.readAsDataURL(fontBlob);

  const base64Data = yield readerPromise;

  yield put({
    type: Actions.SET_FONT_DATA,
    id: font.id,
    psName: font.psName,
    data: base64Data,
  });
}

export function* getCanvasElementFromPoint(point) {
  const state = yield select();
  const { x: clientX, y: clientY } = point;

  // Get preview canvas
  // TODO could there be more than one?
  // TODO don't use document query selector, use a ref.
  const previewCanvasElement = document.querySelector('.preview-canvas');
  if (!previewCanvasElement) {
    // Now sure how'd this happen, but just in case!
    warn('Could not fine preview canvas');
    return null;
  }

  const previewCanvasRect = previewCanvasElement.getBoundingClientRect();

  // Get canvas offset and zoom
  const canvasOffset = UISelectors.canvasOffset(state);
  const zoomLevel = getZoomScale(state);

  // Get container top and left and compute center point
  const previewCanvasX = previewCanvasRect.left;
  const previewCanvasY = previewCanvasRect.top;
  const canvasCenterX =
    previewCanvasX +
    previewCanvasRect.width / 2 +
    canvasOffset.left * zoomLevel;
  const canvasCenterY =
    previewCanvasY +
    previewCanvasRect.height / 2 +
    canvasOffset.top * zoomLevel;

  // If outside of canvas, deselect
  if (UISelectors.isInPreview(state)) {
    const {
      height: creativeHeight,
      width: creativeWidth,
    } = getCreativeRectangle(state);
    const halfCanvasHeight = (creativeHeight * zoomLevel) / 2;
    const halfCanvasWidth = (creativeWidth * zoomLevel) / 2;
    const left = canvasCenterX - halfCanvasWidth;
    const top = canvasCenterY - halfCanvasHeight;
    const right = canvasCenterX + halfCanvasWidth;
    const bottom = canvasCenterY + halfCanvasHeight;
    const isOutOfCanvasClick =
      clientX < left || clientX > right || clientY > bottom || clientY < top;
    if (isOutOfCanvasClick) {
      return null;
    }
  }

  const elementId = DomSelectors.getElementFromPoint(state, {
    x: (clientX - canvasCenterX) / zoomLevel,
    y: (clientY - canvasCenterY) / zoomLevel,
  });

  return elementId;
}

export function* addAShape(shape) {
  const shapeLayer = Layer.createShapeLayer(shape);
  // Persist layer in state
  yield recordForUndo();
  yield put(SHARED_CREATIVE_ACTION_CREATORS.addLayers([shapeLayer]));

  return shapeLayer.id;
}

export function* setCanvasOffset(offset, { deltaX, deltaY }) {
  const state = yield select();
  const scale = getZoomScale(state);

  yield put({
    type: Actions.OFFSET_CANVAS,
    top: offset.top + Math.floor(deltaY / scale),
    left: offset.left + Math.floor(deltaX / scale),
  });
}

export function* resetCanvasOffset() {
  yield put({
    type: Actions.OFFSET_CANVAS,
    top: 0,
    left: 0,
  });
}

export function* resetPlayControls() {
  yield put(PLAYBACK_ACTION_CREATORS.setSpeed(PLAYBACK_SPEEDS.DEFAULT_SPEED));
  yield call(setCurrentFrame, 0);
}

export function* resetCreativeState() {
  FrameManager.cleanup();
  yield call(deselectElement);
  yield call(wipeHistoryClean);
  yield call(resetCanvasOffset);
  yield call(resetPlayControls);
  yield put(REVIEW_ACTION_CREATORS.hideReviewModal());
  yield put(CREATIVE_ACTION_CREATORS.resetImportTemplate());

  const state = yield select();
  const isPublisherContext = UISelectors.isPublisher(state);
  const duration = isPublisherContext
    ? DEFAULT_PUBLISHER_CREATIVE_LENGTH
    : undefined;
  const creativeState = getEmptyCreativeState(duration);
  yield put(
    CREATIVE_ACTION_CREATORS.setCreativeSubstates(
      creativeState.elements,
      creativeState.content,
      creativeState.media,
      creativeState.metadata,
      null,
      {
        creativeId: null,
        creativeTemplateId: null,
      },
    ),
  );

  yield put(CREATIVE_ACTION_CREATORS.creativeLoaded(null));

  yield put(VIDEO_REFORMATTING_ACTION_CREATORS.setActiveShotElementId(null));

  yield put({
    type: Actions.SET_CREATIVE_ID,
    creativeId: null,
  });
}

export function* toggleBasicMode(basicMode) {
  yield put({
    type: Actions.TOGGLE_BASIC_MODE,
    basicMode,
  });
}

/*
 * position and scale to fit the bounding box
 * the concept of bounding box only exists in the Simple Flow
 */
export function* positionToFitBoundingBox(
  elementId,
  bounds,
  previousMediaId,
  mediaId,
) {
  const state = yield select();
  const layer = getLayerByElementId(state, elementId);

  // if layer or bounds don't exist, default to updating element scale
  if (
    !Layer.isLayer(layer) ||
    !Layer.isLayer(layer.originalLayer) ||
    !Rectangle.isValidRectangle(bounds)
  ) {
    yield call(updateElementScale, elementId, previousMediaId, mediaId);
    return;
  }

  // if layer is a display element, it will need transform within the special group
  if (Layer.isDisplayLayer(layer)) {
    yield call(positionToFitDisplayElementToBounds, layer, bounds);
    return;
  }

  const keyframes = Layer.getTransformsToFitLayerToBounds(layer, bounds);
  if (isNotNil(keyframes)) {
    const keyframeKeys = Object.keys(keyframes);
    const updatedKeyframes = {};
    keyframeKeys.forEach((frameKey) => {
      updatedKeyframes[frameKey] = keyframes[frameKey]
        .getCreativeDocumentTransform()
        .toJSON();
    });
    yield put(
      KEYFRAME_MANIPULATION_ACTION_CREATORS.updateKeyframes(
        elementId,
        updatedKeyframes,
      ),
    );
  }

  const { medium, originalLayer } = layer;
  const { fitMode } = originalLayer;
  if (fitMode === FIT_MODES.FILL && medium.mediaType === MEDIA_TYPE.IMAGE) {
    const { centerX } = medium;
    if (isNumber(centerX)) {
      yield smartCenterMediaInElement(elementId);
    }
  }
}

function* positionToFitDisplayElementToBounds(layer, bounds) {
  if (!Layer.isLayer(layer) || !Rectangle.isValidRectangle(bounds)) return;
  const { id: elementId, fitMode, height, medium, width } = layer;

  // replace layer keyframes
  const displayLayerTransform = deepCopy(DEFAULT_KEYFRAME_TRANSFORM);

  // calculate scale
  const mediumRectangle = new Rectangle(0, 0, width, height);
  const scale =
    fitMode === FIT_MODES.FILL
      ? mediumRectangle.getScaleToFillIn(bounds)
      : mediumRectangle.getScaleToFitIn(bounds);

  displayLayerTransform.scale.x = scale;
  displayLayerTransform.scale.y = scale;

  // calculate translation
  let offsetX = 0;
  if (fitMode === FIT_MODES.FILL && medium.mediaType === MEDIA_TYPE.IMAGE) {
    offsetX = getXTransformForCentering(medium, scale, bounds.width);
  }

  displayLayerTransform.translation.x = bounds.x + offsetX;
  displayLayerTransform.translation.y = bounds.y;

  yield put(
    KEYFRAME_MANIPULATION_ACTION_CREATORS.updateKeyframes(elementId, {
      [DEFAULT_KEYFRAME_NUMBER]: displayLayerTransform,
    }),
  );
}

/** Since we are positioning it relative to bounding box, `getXTransformForCentering`
 * returns transform that guarantees that we never see whitespace in animation.
 * We correspondingly apply this transformation to all the frames.In the current
 * implementation the first frame will be perfectly centered according to the saliency algorithm.
 */
export function* smartCenterMediaInElement(elementId) {
  const state = yield select();
  const layer = getLayerByElementId(state, elementId);

  const { medium: media, originalLayer } = layer;
  const bounds = getBoundingBoxByElementId(state, elementId);
  const { width: containerWidth } = bounds;
  const mediaRec = new Rectangle(0, 0, media.width, media.height);
  const scale = mediaRec.getScaleToFillIn(bounds);

  const transformX =
    getXTransformForCentering(media, scale, containerWidth) *
    originalLayer.canvasSpaceTransform.scale.x;

  yield put({
    type: Actions.MOVE_ELEMENT,
    applyToAll: true,
    currentFrame: -1,
    transformElementId: elementId,
    x: transformX,
    y: 0,
  });
}

export function* setCreativeTemplate(template = null) {
  yield put({
    type: Actions.SET_CREATIVE_TEMPLATE,
    template,
  });
}

function* callImportDiscoverAssetAPI(discoverAssetId) {
  const state = yield select();
  const currentUserContext = UISelectors.getCurrentUserContext(state);
  return yield call(
    API.importDiscoverAsset,
    discoverAssetId,
    currentUserContext,
  );
}

export function* importDiscoverAsset(
  importAssetId,
  trimMode,
  assignedCreativeFieldId = IMPORTED_SNAP_BACKGROUND_CREATIVE_FIELD,
) {
  // Import the asset to the media
  const { data, success: importSuccess } = yield call(
    callImportDiscoverAssetAPI,
    importAssetId,
  );

  const serverId = data && data.serverId;

  if (!importSuccess || !serverId) {
    throw new ImportDiscoverAssetError();
  }

  yield fork(addInitialElementWithServerMedia, serverId, false);

  const { contentId, elementId } = yield take(
    IMPORTED_ASSET_ACTION_CREATORS.importCompleted,
  );

  if (isNotNil(contentId)) {
    // Once the media is imported we can assign to the specified field
    yield put(
      CONTENT_ACTION_CREATORS.setCreativeFieldId(
        contentId,
        assignedCreativeFieldId,
      ),
    );

    const state = yield select();
    if (isLoadingCreativeFields(state)) {
      yield take(CREATIVE_CONTENT_ACTION_CREATORS.finishLoadingCreativeFields);
    }

    yield put(
      CREATIVE_CONTENT_ACTION_CREATORS.updateCreativeFieldWithContent(
        elementId,
      ),
    );

    // If the option to open the clip editor is set, open it
    const updatedState = yield select();
    if (
      trimMode &&
      ContentSelectors.getTypeById(updatedState, contentId) ===
        CONTENT_TYPE.VIDEO
    ) {
      // Set trim mode in state.
      // Open trim dialog.
      yield put(
        MEDIA_TRIMMER_ACTION_CREATORS.openMediaTrimmer(contentId, true, true),
      );
    }
  }
}

export function* setIsTransformedByUser(elementId, isTransformedByUser = true) {
  yield put({
    type: Actions.SET_TRANSFORMED_BY_USER,
    elementId,
    isTransformedByUser,
  });
}
